import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnInit,
  output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';

import { AppSharedModule } from 'app/app-shared.module';
import {
  LexXiTreePickerComponent,
  TreePickerItem,
} from 'app/components/xi-components/lex-xi-tree-picker/lex-xi-tree-picker.component';

import { Collection } from 'app/providers/storage-api/collection.service';

import { ProjectService, Project } from 'app/providers/storage-api/project.service';
import {
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

export interface ConfigurationCollectionSelectorData {
  selectedCollectionName: string;
}

@Component({
  selector: 'app-configuration-collection-selector',
  imports: [AppSharedModule, LexXiTreePickerComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './configuration-collection-selector.component.html',
  styleUrl: './configuration-collection-selector.component.scss',
})
export class ConfigurationCollectionSelectorComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  private _projectService = inject(ProjectService);

  isShowing = input<boolean>(false);

  modalClosed = output<{
    confirmed: boolean;
    selectedCollection: Collection;
  }>();

  isLoading: WritableSignal<boolean> = signal<boolean>(false);
  confirmable: Signal<boolean> = computed(
    () => !this.isLoading() && this.selectedCollection() !== null
  );
  projects: WritableSignal<Project[]> = signal<Project[]>([]);
  items: Signal<TreePickerItem[]> = computed(() => {
    const res = this.projects().map((project) => {
      const options = project.collections.map((collection) => ({
        id: collection.collectionName,
        title: collection.displayName,
      }));
      return {
        id: project.id,
        title: project.name,
        options: options,
      };
    });
    return res;
  });
  selectedCollection: WritableSignal<Collection | null> = signal<Collection | null>(null);

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);

    // Load projects
    this.projects.set((await this._projectService.getProjects())?.items ?? []);

    this.isLoading.set(false);
  }

  onCollectionSelected(selectedIds: string[]): void {
    let selectedCollection = null;
    if (selectedIds) {
      selectedCollection = this.projects()
        .flatMap((project) => project.collections)
        .find((c) => selectedIds.includes(c.collectionName));
    }
    this.selectedCollection.set(selectedCollection ?? null);
  }

  cancel(): void {
    this.modalClosed.emit({ confirmed: false, selectedCollection: null });
  }

  confirm(): void {
    this.modalClosed.emit({ confirmed: true, selectedCollection: this.selectedCollection() });
  }
}
